import type { ApClassApi } from "../../api/ApClassApi";
import type { ApClassAttendanceFetchOptions } from "../../generated/dto/ApClassAttendanceFetchOptions";
import type { ApClassAttendanceStaff } from "../../generated/dto/ApClassAttendanceStaff";
import type { ApClassFetchOptions } from "../../generated/dto/ApClassFetchOptions";
import type { ApClassFormatFetchOptions } from "../../generated/dto/ApClassFormatFetchOptions";
import type { ApClassFormatStaff } from "../../generated/dto/ApClassFormatStaff";
import type { ApClassMember } from "../../generated/dto/ApClassMember";
import type { ApClassRef } from "../../generated/dto/ApClassRef";
import type { ApClassSearchParams } from "../../generated/dto/ApClassSearchParams";
import type { ApClassSessionFetchOptions } from "../../generated/dto/ApClassSessionFetchOptions";
import type { ApClassSessionSearchParams } from "../../generated/dto/ApClassSessionSearchParams";
import type { ApClassSessionStaff } from "../../generated/dto/ApClassSessionStaff";
import type { ApClassSessionStaffSearchResult } from "../../generated/dto/ApClassSessionStaffSearchResult";
import type { ApClassSignupStaff } from "../../generated/dto/ApClassSignupStaff";
import type { ApClassSignupStaffFetchOptions } from "../../generated/dto/ApClassSignupStaffFetchOptions";
import type { ApClassStaff } from "../../generated/dto/ApClassStaff";
import type { ApClassStaffSearchResult } from "../../generated/dto/ApClassStaffSearchResult";
import type { ApClassTypeFetchOptions } from "../../generated/dto/ApClassTypeFetchOptions";
import type { ApClassTypeOverrideFetchOptions } from "../../generated/dto/ApClassTypeOverrideFetchOptions";
import type { ApClassTypeOverrideStaff } from "../../generated/dto/ApClassTypeOverrideStaff";
import type { ApClassTypeSearchParams } from "../../generated/dto/ApClassTypeSearchParams";
import type { ApClassTypeStaff } from "../../generated/dto/ApClassTypeStaff";
import type { ApClassTypeStaffSearchResult } from "../../generated/dto/ApClassTypeStaffSearchResult";
import type { ApClassWlResultFetchOptions } from "../../generated/dto/ApClassWlResultFetchOptions";
import type { ApClassWlResultStaff } from "../../generated/dto/ApClassWlResultStaff";
import type { ClassLocationFetchOptions } from "../../generated/dto/ClassLocationFetchOptions";
import type { ClassLocationStaff } from "../../generated/dto/ClassLocationStaff";
import type { CreateApClass } from "../../generated/dto/CreateApClass";
import type { CreateApClassAttendance } from "../../generated/dto/CreateApClassAttendance";
import type { CreateApClassFormat } from "../../generated/dto/CreateApClassFormat";
import type { CreateApClassSession } from "../../generated/dto/CreateApClassSession";
import type { CreateApClassSignup } from "../../generated/dto/CreateApClassSignup";
import type { CreateApClassType } from "../../generated/dto/CreateApClassType";
import type { CreateApClassTypeOverride } from "../../generated/dto/CreateApClassTypeOverride";
import type { CreateApClassWlResult } from "../../generated/dto/CreateApClassWlResult";
import type { DeleteApClassAttendanceStaff } from "../../generated/dto/DeleteApClassAttendanceStaff";
import type { DeleteApClassFormatStaff } from "../../generated/dto/DeleteApClassFormatStaff";
import type { DeleteApClassSessionStaff } from "../../generated/dto/DeleteApClassSessionStaff";
import type { DeleteApClassSignupStaff } from "../../generated/dto/DeleteApClassSignupStaff";
import type { DeleteApClassStaff } from "../../generated/dto/DeleteApClassStaff";
import type { DeleteApClassTypeOverrideStaff } from "../../generated/dto/DeleteApClassTypeOverrideStaff";
import type { DeleteApClassTypeStaff } from "../../generated/dto/DeleteApClassTypeStaff";
import type { DeleteApClassWlResultStaff } from "../../generated/dto/DeleteApClassWlResultStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { UpdateApClass } from "../../generated/dto/UpdateApClass";
import type { UpdateApClassAttendance } from "../../generated/dto/UpdateApClassAttendance";
import type { UpdateApClassFormat } from "../../generated/dto/UpdateApClassFormat";
import type { UpdateApClassSession } from "../../generated/dto/UpdateApClassSession";
import type { UpdateApClassSignup } from "../../generated/dto/UpdateApClassSignup";
import type { UpdateApClassType } from "../../generated/dto/UpdateApClassType";
import type { UpdateApClassTypeOverride } from "../../generated/dto/UpdateApClassTypeOverride";
import type { UpdateApClassWlResult } from "../../generated/dto/UpdateApClassWlResult";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class ApClassFrontendService {
  constructor(private readonly api: ApClassApi) {}
  async getApClassTypeStaff(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    return this.api.getApClassTypeStaff(id, options)
  }
  async getApClassTypeOverrideStaff(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<ApClassTypeOverrideStaff> {
    return this.api.getApClassTypeOverrideStaff(id, options)
  }
  async getApClassFormatStaff(id: number, options?: ApClassFormatFetchOptions): Promise<ApClassFormatStaff> {
    return this.api.getApClassFormatStaff(id, options)
  }
  async getApClassStaff(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    return this.api.getApClassStaff(id, options)
  }
  async getApClassSessionStaff(id: number, options?: ApClassSessionFetchOptions): Promise<ApClassSessionStaff> {
    return this.api.getApClassSessionStaff(id, options)
  }
  async getApClassSignupStaff(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff> {
    return this.api.getApClassSignupStaff(id, options)
  }
  async getApClassAttendanceStaff(id: number, options?: ApClassAttendanceFetchOptions): Promise<ApClassAttendanceStaff> {
    return this.api.getApClassAttendanceStaff(id, options)
  }
  async getApClassWlResultStaff(id: number, options?: ApClassWlResultFetchOptions): Promise<ApClassWlResultStaff> {
    return this.api.getApClassWlResultStaff(id, options)
  }
  async createApClassTypeStaff(source: CreateApClassType): Promise<ApClassTypeStaff> {
    return this.api.createApClassTypeStaff(source)
  }
  async createApClassTypeOverrideStaff(source: CreateApClassTypeOverride): Promise<ApClassTypeOverrideStaff> {
    return this.api.createApClassTypeOverrideStaff(source)
  }
  async createApClassFormatStaff(source: CreateApClassFormat): Promise<ApClassFormatStaff> {
    return this.api.createApClassFormatStaff(source)
  }
  async createApClassStaff(source: CreateApClass): Promise<ApClassStaff> {
    return this.api.createApClassStaff(source)
  }
  async createApClassSessionStaff(source: CreateApClassSession): Promise<ApClassSessionStaff> {
    return this.api.createApClassSessionStaff(source)
  }
  async createApClassSignupStaff(source: CreateApClassSignup): Promise<ApClassSignupStaff> {
    return this.api.createApClassSignupStaff(source)
  }
  async createApClassAttendanceStaff(source: CreateApClassAttendance): Promise<ApClassAttendanceStaff> {
    return this.api.createApClassAttendanceStaff(source)
  }
  async createApClassWlResultStaff(source: CreateApClassWlResult): Promise<ApClassWlResultStaff> {
    return this.api.createApClassWlResultStaff(source)
  }
  async updateApClassTypeStaff(source: UpdateApClassType): Promise<ApClassTypeStaff> {
    return this.api.updateApClassTypeStaff(source)
  }
  async updateApClassTypeOverrideStaff(source: UpdateApClassTypeOverride): Promise<ApClassTypeOverrideStaff> {
    return this.api.updateApClassTypeOverrideStaff(source)
  }
  async updateApClassFormatStaff(source: UpdateApClassFormat): Promise<ApClassFormatStaff> {
    return this.api.updateApClassFormatStaff(source)
  }
  async updateApClassStaff(source: UpdateApClass): Promise<ApClassStaff> {
    return this.api.updateApClassStaff(source)
  }
  async updateApClassSessionStaff(source: UpdateApClassSession): Promise<ApClassSessionStaff> {
    return this.api.updateApClassSessionStaff(source)
  }
  async updateApClassSignupStaff(source: UpdateApClassSignup): Promise<ApClassSignupStaff> {
    return this.api.updateApClassSignupStaff(source)
  }
  async updateApClassAttendanceStaff(source: UpdateApClassAttendance): Promise<ApClassAttendanceStaff> {
    return this.api.updateApClassAttendanceStaff(source)
  }
  async updateApClassWlResultStaff(source: UpdateApClassWlResult): Promise<ApClassWlResultStaff> {
    return this.api.updateApClassWlResultStaff(source)
  }
  async deleteApClassTypeStaff(source: DeleteApClassTypeStaff): Promise<void> {
    return this.api.deleteApClassTypeStaff(source)
  }
  async deleteApClassTypeOverrideStaff(source: DeleteApClassTypeOverrideStaff): Promise<void> {
    return this.api.deleteApClassTypeOverrideStaff(source)
  }
  async deleteApClassFormatStaff(source: DeleteApClassFormatStaff): Promise<void> {
    return this.api.deleteApClassFormatStaff(source)
  }
  async deleteApClassStaff(source: DeleteApClassStaff): Promise<void> {
    return this.api.deleteApClassStaff(source)
  }
  async deleteApClassSessionStaff(source: DeleteApClassSessionStaff): Promise<void> {
    return this.api.deleteApClassSessionStaff(source)
  }
  async deleteApClassSignupStaff(source: DeleteApClassSignupStaff): Promise<void> {
    return this.api.deleteApClassSignupStaff(source)
  }
  async deleteApClassAttendanceStaff(source: DeleteApClassAttendanceStaff): Promise<void> {
    return this.api.deleteApClassAttendanceStaff(source)
  }
  async deleteApClassWlResultStaff(source: DeleteApClassWlResultStaff): Promise<void> {
    return this.api.deleteApClassWlResultStaff(source)
  }
  async searchApClassTypeStaff(params: ApClassTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassTypeStaffSearchResult>> {
    return this.api.searchApClassTypeStaff(params, page, perPage)
  }
  async searchApClassStaff(params: ApClassSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassStaffSearchResult>> {
    return this.api.searchApClassStaff(params, page, perPage)
  }
  async searchApClassSessionStaff(params: ApClassSessionSearchParams, page: number, perPage: number): Promise<PaginationResult<ApClassSessionStaffSearchResult>> {
    return this.api.searchApClassSessionStaff(params, page, perPage)
  }
  async searchApClassTypeStaffFull(params: ApClassTypeSearchParams, page: number, perPage: number, options?: ApClassTypeFetchOptions): Promise<PaginationResult<ApClassTypeStaff>> {
    return this.api.searchApClassTypeStaffFull(params, page, perPage, options)
  }
  async searchApClassStaffFull(params: ApClassSearchParams, page: number, perPage: number, options?: ApClassFetchOptions): Promise<PaginationResult<ApClassStaff>> {
    return this.api.searchApClassStaffFull(params, page, perPage, options)
  }
  async searchApClassSessionStaffFull(params: ApClassSessionSearchParams, page: number, perPage: number, options?: ApClassSessionFetchOptions): Promise<PaginationResult<ApClassSessionStaff>> {
    return this.api.searchApClassSessionStaffFull(params, page, perPage, options)
  }
  async getApClassTypeStaffRatingPrereqId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getApClassTypeStaffRatingPrereqId(id, options)
  }
  async getApClassTypeStaffRatingOverkillId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getApClassTypeStaffRatingOverkillId(id, options)
  }
  async getApClassTypeStaffClassPrereqId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff | null> {
    return this.api.getApClassTypeStaffClassPrereqId(id, options)
  }
  async getApClassTypeStaffClassOverkillId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff | null> {
    return this.api.getApClassTypeStaffClassOverkillId(id, options)
  }
  async getApClassTypeStaffTeachRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    return this.api.getApClassTypeStaffTeachRatingId(id, options)
  }
  async getApClassTypeStaffFormats(id: number, options?: ApClassFormatFetchOptions): Promise<Array<ApClassFormatStaff>> {
    return this.api.getApClassTypeStaffFormats(id, options)
  }
  async getApClassTypeStaffTypesRequiringThisAsPrereq(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    return this.api.getApClassTypeStaffTypesRequiringThisAsPrereq(id, options)
  }
  async getApClassTypeStaffTypesRequiringThisAsOverkill(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    return this.api.getApClassTypeStaffTypesRequiringThisAsOverkill(id, options)
  }
  async getApClassTypeStaffClasses(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    return this.api.getApClassTypeStaffClasses(id, options)
  }
  async getApClassTypeStaffOverrides(id: number, options?: ApClassTypeOverrideFetchOptions): Promise<Array<ApClassTypeOverrideStaff>> {
    return this.api.getApClassTypeStaffOverrides(id, options)
  }
  async getApClassTypeOverrideStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getApClassTypeOverrideStaffPersonId(id, options)
  }
  async getApClassTypeOverrideStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    return this.api.getApClassTypeOverrideStaffTypeId(id, options)
  }
  async getApClassFormatStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    return this.api.getApClassFormatStaffTypeId(id, options)
  }
  async getApClassFormatStaffInstances(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    return this.api.getApClassFormatStaffInstances(id, options)
  }
  async getApClassStaffFormatId(id: number, options?: ApClassFormatFetchOptions): Promise<ApClassFormatStaff | null> {
    return this.api.getApClassStaffFormatId(id, options)
  }
  async getApClassStaffLocationId(id: number, options?: ClassLocationFetchOptions): Promise<ClassLocationStaff | null> {
    return this.api.getApClassStaffLocationId(id, options)
  }
  async getApClassStaffInstructorId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getApClassStaffInstructorId(id, options)
  }
  async getApClassStaffSessions(id: number, options?: ApClassSessionFetchOptions): Promise<Array<ApClassSessionStaff>> {
    return this.api.getApClassStaffSessions(id, options)
  }
  async getApClassStaffSignups(id: number, options?: ApClassSignupStaffFetchOptions): Promise<Array<ApClassSignupStaff>> {
    return this.api.getApClassStaffSignups(id, options)
  }
  async getApClassStaffTypeId(id: number, options?: ApClassTypeFetchOptions): Promise<ApClassTypeStaff> {
    return this.api.getApClassStaffTypeId(id, options)
  }
  async getApClassSessionStaffInstanceId(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    return this.api.getApClassSessionStaffInstanceId(id, options)
  }
  async getApClassSessionStaffAttendance(id: number, options?: ApClassAttendanceFetchOptions): Promise<Array<ApClassAttendanceStaff>> {
    return this.api.getApClassSessionStaffAttendance(id, options)
  }
  async getApClassSignupStaffWlResult(id: number, options?: ApClassWlResultFetchOptions): Promise<ApClassWlResultStaff | null> {
    return this.api.getApClassSignupStaffWlResult(id, options)
  }
  async getApClassSignupStaffInstanceId(id: number, options?: ApClassFetchOptions): Promise<ApClassStaff> {
    return this.api.getApClassSignupStaffInstanceId(id, options)
  }
  async getApClassSignupStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    return this.api.getApClassSignupStaffPersonId(id, options)
  }
  async getApClassSignupStaffAttendance(id: number, options?: ApClassAttendanceFetchOptions): Promise<Array<ApClassAttendanceStaff>> {
    return this.api.getApClassSignupStaffAttendance(id, options)
  }
  async getApClassAttendanceStaffSessionId(id: number, options?: ApClassSessionFetchOptions): Promise<ApClassSessionStaff | null> {
    return this.api.getApClassAttendanceStaffSessionId(id, options)
  }
  async getApClassAttendanceStaffSignupId(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff | null> {
    return this.api.getApClassAttendanceStaffSignupId(id, options)
  }
  async getApClassWlResultStaffSignupId(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff> {
    return this.api.getApClassWlResultStaffSignupId(id, options)
  }
  async getClassInstanceStaff(ref: ApClassRef): Promise<ApClassStaff> {
    return this.api.getClassInstanceStaff(ref)
  }
  async getClassInstanceMember(ref: ApClassRef): Promise<ApClassMember> {
    return this.api.getClassInstanceMember(ref)
  }
  async summaryLabelStaff(target: ApClassRef): Promise<string> {
    return this.api.summaryLabelStaff(target)
  }
  async summaryLabelMember(target: ApClassRef): Promise<string> {
    return this.api.summaryLabelMember(target)
  }
}
